import ReactEChartsCore from 'echarts-for-react/lib/core';
import { ChartContainer } from '../../analytics/shared/ChartContainer';
import { EmptyState } from '../../analytics/shared/EmptyState';
import { echarts } from '../../analytics/shared/echarts-config';
import { buildDonutOptions, buildHorizontalBarOptions } from '../../analytics/shared/chart-option-builders';
import type {
  EngagementMetricsFpSpMessageType,
  EngagementMetricsReferralBreakdown,
} from '../../../types/engagement-metrics.types';

type MessageAndReferralBreakdownsProps = {
  byFpSpMessageType: EngagementMetricsFpSpMessageType[];
  referralBreakdown: EngagementMetricsReferralBreakdown;
};

const BILLABLE_COLOR = '#2f7d5e';
const NON_BILLABLE_COLOR = '#aab2ab';
const REFERRAL_COLORS = ['#15543e', '#3a8f93'];

const buildMessageTypeOptions = (data: EngagementMetricsFpSpMessageType[]) =>
  buildHorizontalBarOptions(
    data.map((item) => ({
      label: item.label,
      count: item.count,
      color: item.isBillable ? BILLABLE_COLOR : NON_BILLABLE_COLOR,
    })),
    130,
  );

const buildReferralOptions = (data: EngagementMetricsReferralBreakdown) =>
  buildDonutOptions(
    data.bySource.map((item, index) => ({
      name: item.label,
      value: item.count,
      color: REFERRAL_COLORS[index % REFERRAL_COLORS.length],
    })),
  );

export const MessageAndReferralBreakdowns = ({
  byFpSpMessageType,
  referralBreakdown,
}: MessageAndReferralBreakdownsProps) => {
  const hasMessageTypeData = byFpSpMessageType.some((item) => item.count > 0);
  const hasReferralData = referralBreakdown.periodTotal > 0;

  return (
    <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
      <ChartContainer title="FP → SP Message Types" subtitle="Only “New request” is billable">
        {hasMessageTypeData ? (
          <ReactEChartsCore
            echarts={echarts}
            option={buildMessageTypeOptions(byFpSpMessageType)}
            style={{ height: 260 }}
            notMerge
            lazyUpdate
          />
        ) : (
          <EmptyState message="No FP→SP message-type data available." />
        )}
      </ChartContainer>

      <ChartContainer
        title="Referral Sources"
        subtitle={`${referralBreakdown.periodTotal} referrals this period`}
      >
        {hasReferralData ? (
          <ReactEChartsCore
            echarts={echarts}
            option={buildReferralOptions(referralBreakdown)}
            style={{ height: 260 }}
            notMerge
            lazyUpdate
          />
        ) : (
          <EmptyState message="No referral-source data available." />
        )}
      </ChartContainer>
    </div>
  );
};
